import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ThumbsUp, Pin, X } from "lucide-react";
import posthog from "@/lib/posthog";
import { SiGooglemeet, SiZoom } from "react-icons/si";

// Microsoft Teams inline SVG (not in react-icons/si)
function TeamsIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none">
      <path d="M15 6a2.5 2.5 0 110-5 2.5 2.5 0 010 5zm3.5 1.5h-4.25A3.75 3.75 0 0010.5 11v5.5a.5.5 0 00.5.5h7.5a.5.5 0 00.5-.5V9a1.5 1.5 0 00-1.5-1.5zM9.5 9.5a2 2 0 10-4 0 2 2 0 004 0zM6 12.5A2.5 2.5 0 003.5 15v3a.5.5 0 00.5.5h5a.5.5 0 00.5-.5v-3A2.5 2.5 0 006 12.5z" fill="#6264A7"/>
    </svg>
  );
}

const PLATFORMS = [
  { name: "Google Meet", Icon: () => <SiGooglemeet className="w-4 h-4" style={{ color: "#00897B" }} />, color: "#00897B" },
  { name: "Zoom",        Icon: () => <SiZoom       className="w-4 h-4" style={{ color: "#2D8CFF" }} />, color: "#2D8CFF" },
  { name: "Teams",       Icon: () => <TeamsIcon     className="w-4 h-4" />,                              color: "#6264A7" },
];

// ─── Real-time intelligence that helps close deals ───────────────
const SCENARIOS = [
  {
    id: 1,
    speaker: "Lara M.",
    role: "IT Security",
    quote: "Where is our call data stored? We're based in Europe.",
    trigger: "Where is our call data stored",
    card: {
      dot: "bg-teal-500",
      accent: "border-l-teal-400",
      label: "Live Answer",
      response: "Hosted on AWS US-East. EU data residency is available on the Enterprise tier.",
      source: "Privacy Policy",
    },
  },
  {
    id: 2,
    speaker: "Jordan L.",
    role: "Head of RevOps",
    quote: "We need this live sooner than we discussed before.",
    trigger: "discussed before",
    card: {
      dot: "bg-blue-500",
      accent: "border-l-blue-400",
      label: "Past Meeting",
      response: "From your Mar 3 call: Q3 was the agreed target. Ask what moved the deadline — it's a buying signal.",
      source: "Call · Mar 3",
    },
  },
  {
    id: 3,
    speaker: "Mark T.",
    role: "Sales Manager",
    quote: "I'll need to loop in my VP before anything can move.",
    trigger: "loop in my VP",
    card: {
      dot: "bg-violet-500",
      accent: "border-l-violet-400",
      label: "Follow Up",
      response: "What does your VP need to see to feel confident about this?",
    },
  },
  {
    id: 4,
    speaker: "David L.",
    role: "CFO",
    quote: "We already have a recording tool in place.",
    trigger: "recording tool in place",
    card: {
      dot: "bg-amber-500",
      accent: "border-l-amber-400",
      label: "Defense",
      response: "Shift the question — what happens after the call ends? That's where deals are won or lost.",
    },
  },
];

// ─── Timing (ms) — slow enough to read at a glance ───────────────
const SP = {
  HIGHLIGHT: 1300, // trigger phrase highlights
  SCAN:      2100, // "analyzing…" appears
  CARD:      2900, // card fades in
  FADE_OUT:  7800, // scenario starts fading
  NEXT:      8400, // next scenario begins
};

// ─── SmartCard — exact SmartCard.js markup, no Framer Motion ─────
// Parent controls visibility via CSS — no internal animation
function SmartCardMock({ card }) {
  return (
    <div
      className={`
        bg-white rounded border border-[hsl(240,5%,91%)] border-l-[3px] ${card.accent}
        px-5 py-4 shadow-[0_2px_20px_-2px_rgba(0,0,0,0.09)]
      `}
    >
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full shrink-0 ${card.dot}`} />
          <span className="text-[11px] font-medium uppercase tracking-wide text-[hsl(230,8%,55%)]">
            {card.label}
          </span>
          <span className="text-[hsl(230,8%,78%)]">·</span>
          <span className="text-[11px] font-semibold text-[hsl(228,25%,35%)]">Now</span>
        </div>
        <div className="flex items-center gap-0.5 opacity-20">
          <span className="p-1.5"><ThumbsUp className="w-3.5 h-3.5 text-[hsl(230,8%,55%)]" /></span>
          <span className="p-1.5"><Pin      className="w-3.5 h-3.5 text-[hsl(230,8%,55%)]" /></span>
          <span className="p-1.5"><X        className="w-3.5 h-3.5 text-[hsl(230,8%,55%)]" /></span>
        </div>
      </div>
      {/* text-xl leading-[1.45] — exact SmartCard.js */}
      <p className="text-[1.05rem] leading-[1.45] tracking-[-0.01em] text-[hsl(228,25%,15%)] font-medium">
        "{card.response}"
      </p>
      {/* Source chip — matches grounded card in SmartCard.js */}
      {card.source && (
        <div className="mt-2.5">
          <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-teal-500/10 text-teal-700 font-medium">
            from: {card.source}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── macOS traffic lights ─────────────────────────────────────────
function TrafficLights() {
  return (
    <div className="flex items-center gap-[6px]">
      <div className="h-[11px] w-[11px] rounded-full bg-[#FF5F57]" />
      <div className="h-[11px] w-[11px] rounded-full bg-[#FEBC2E]" />
      <div className="h-[11px] w-[11px] rounded-full bg-[#28C840]" />
    </div>
  );
}

// ─── CSS easing helpers ───────────────────────────────────────────
const EASE_OUT = "cubic-bezier(0.22, 1, 0.36, 1)";

// ─── Desktop app window ───────────────────────────────────────────
function DesktopAppWindow() {
  const [scenarioIdx, setScenarioIdx] = useState(0);
  // subPhase: 0 = line visible | 1 = highlight | 2 = scan | 3 = card
  const [subPhase, setSubPhase] = useState(0);
  const [fading,   setFading]   = useState(false);

  useEffect(() => {
    // Reset to clean state immediately when scenario changes
    setSubPhase(0);
    setFading(false);

    const ids = [
      setTimeout(() => setSubPhase(1), SP.HIGHLIGHT),
      setTimeout(() => setSubPhase(2), SP.SCAN),
      setTimeout(() => setSubPhase(3), SP.CARD),
      setTimeout(() => setFading(true),                                    SP.FADE_OUT),
      setTimeout(() => setScenarioIdx(i => (i + 1) % SCENARIOS.length),   SP.NEXT),
    ];
    return () => ids.forEach(clearTimeout);
  }, [scenarioIdx]);

  const s = SCENARIOS[scenarioIdx];
  const parts = s.quote.split(s.trigger); // [before, after]

  // ── CSS transition values derived from subPhase ──────────────
  const highlightBg    = subPhase >= 1 ? "rgba(0,146,180,0.12)" : "transparent";
  const highlightColor = subPhase >= 1 ? "hsl(198,100%,26%)"    : "hsl(230,8%,38%)";
  const scanOpacity    = subPhase === 2 ? 1 : 0;
  const cardOpacity    = subPhase >= 3  ? 1 : 0;
  const cardTranslate  = subPhase >= 3  ? "translateY(0)"        : "translateY(10px)";
  const hintOpacity    = subPhase >= 3  ? 0 : 0.38;

  return (
    <div
      className="w-full overflow-hidden rounded-2xl border border-black/[0.07]"
      style={{
        height: "400px",
        boxShadow: "0 28px 64px -10px rgba(0,0,0,0.22), 0 6px 18px -4px rgba(0,0,0,0.10)",
      }}
    >
      {/* ── macOS title bar ── */}
      <div
        className="relative flex shrink-0 items-center justify-between px-4 py-3 border-b border-black/[0.06]"
        style={{
          background: [
            "radial-gradient(ellipse 140% 200% at 70% -20%, rgba(200,130,80,0.18), transparent 55%)",
            "hsl(40,22%,92%)",
          ].join(", "),
        }}
      >
        <TrafficLights />
        <div className="absolute inset-x-0 flex justify-center pointer-events-none">
          <img
            src="/images/landing-v2/hero/shadow-wordmark.svg"
            alt="Shadow"
            className="h-[10px] w-auto opacity-30"
            draggable="false"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-60" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
          </span>
          <span className="text-[11px] font-semibold text-emerald-700">Listening</span>
        </div>
      </div>

      {/* ── App body — warm mesh-bg, fixed height, no overflow ── */}
      <div
        className="flex flex-col px-5 pt-4 pb-5"
        style={{
          height: "357px", // 400 − 43 (title bar)
          background: [
            "radial-gradient(ellipse 90% 70% at 85% 5%,  rgba(204,130,80,0.12), transparent 55%)",
            "radial-gradient(ellipse 70% 60% at 10% 95%, rgba(185,110,60,0.08), transparent 60%)",
            "hsl(40,14%,96%)",
          ].join(", "),
          // Outer fade between scenarios — pure CSS, no Framer Motion
          opacity:    fading ? 0 : 1,
          transition: fading ? "opacity 0.55s ease" : "opacity 0.3s ease",
        }}
      >
        {/* Meeting header */}
        <div className="shrink-0 flex items-center gap-2 mb-4">
          <span className="relative flex h-2 w-2 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          </span>
          <span className="text-[12.5px] font-semibold text-[hsl(228,25%,15%)] truncate">
            Live · Acme Corp — Enterprise Discovery
          </span>
        </div>

        {/* ── Transcript block — shrink-0, no layout shift ── */}
        <div className="shrink-0">
          {/* Speaker row */}
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-[12px] font-semibold text-[hsl(228,25%,20%)]">{s.speaker}</span>
            <span className="text-[11px] text-[hsl(230,8%,58%)]">{s.role}</span>
          </div>

          {/* Quote with CSS-transitioned trigger highlight — no layout shift */}
          <p className="text-[13.5px] leading-relaxed text-[hsl(230,8%,38%)]">
            {parts[0]}
            <span
              className="rounded-sm px-0.5 font-medium"
              style={{
                backgroundColor: highlightBg,
                color:            highlightColor,
                transition:       `background-color 0.6s ${EASE_OUT}, color 0.6s ${EASE_OUT}`,
              }}
            >
              {s.trigger}
            </span>
            {parts[1]}
          </p>

          {/* "Shadow is analyzing…" — FIXED HEIGHT SLOT, CSS opacity only */}
          {/* Height always reserved → zero layout shift */}
          <div
            style={{
              height:     "24px",
              marginTop:  "8px",
              opacity:    scanOpacity,
              transition: `opacity 0.35s ease`,
            }}
            className="flex items-center gap-1.5"
          >
            <span className="relative flex h-1.5 w-1.5 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0092B4] opacity-60" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#0092B4]" />
            </span>
            <span className="text-[11px] font-medium text-[#0092B4]">Shadow is analyzing…</span>
          </div>
        </div>

        {/* ── Card slot — flex-1, both layers absolutely positioned ── */}
        {/* Nothing in this area ever changes height → zero layout shift */}
        <div className="flex-1 relative mt-2">

          {/* "Listening" hint — fades out when card appears */}
          <div
            className="absolute bottom-0 left-0 right-0 flex items-center gap-2"
            style={{
              opacity:        hintOpacity,
              transition:     "opacity 0.45s ease",
              pointerEvents:  "none",
            }}
          >
            <span className="relative flex h-1.5 w-1.5 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[hsl(228,25%,50%)] opacity-60" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[hsl(228,25%,50%)]" />
            </span>
            <span className="text-[12px] italic text-[hsl(228,25%,40%)]">Shadow is listening…</span>
          </div>

          {/* SmartCard — CSS fade + translate, keyed per scenario for fresh render */}
          <div
            key={scenarioIdx}
            className="absolute bottom-0 left-0 right-0"
            style={{
              opacity:    cardOpacity,
              transform:  cardTranslate,
              transition: `opacity 0.5s ${EASE_OUT}, transform 0.55s ${EASE_OUT}`,
              pointerEvents: subPhase >= 3 ? "auto" : "none",
            }}
          >
            <SmartCardMock card={s.card} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Hero section ─────────────────────────────────────────────────
export default function HeroSection() {
  return (
    <section id="home" className="relative scroll-mt-0 overflow-hidden bg-[#070D1A]">
      {/* Dot grid */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.045) 1px, transparent 1px)",
          backgroundSize:  "32px 32px",
        }}
      />
      {/* Atmospheric glows */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute right-0 top-0 h-[700px] w-[700px] -translate-y-1/3 translate-x-1/3 rounded-full bg-[#0092B4]/[0.15] blur-[130px]" />
        <div className="absolute left-0 bottom-0 h-[500px] w-[500px] translate-y-1/3 -translate-x-1/3 rounded-full bg-[#0092B4]/[0.10] blur-[120px]" />
      </div>

      <div className="relative mx-auto w-full max-w-7xl px-4 pb-20 pt-12 sm:px-6 sm:pb-24 sm:pt-16 lg:px-8 lg:pb-28 lg:pt-20">
        {/* Product Hunt badge */}
        <div className="mb-10 flex justify-center">
          <a
            href="https://www.producthunt.com/products/shadow-6?embed=true&utm_source=badge-top-post-badge&utm_medium=badge&utm_campaign=badge-shadow-2-0-2"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              alt="Shadow on Product Hunt"
              width="200"
              height="43"
              src="https://api.producthunt.com/widgets/embed-image/v1/top-post-badge.svg?post_id=1136553&theme=dark&period=daily&t=1778147887528"
            />
          </a>
        </div>

        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2 lg:gap-16">
          {/* ── Copy ── */}
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#0092B4]/25 bg-[#0092B4]/10 px-3.5 py-1.5 mb-6">
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0092B4] opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#0092B4]" />
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-widest text-[#0092B4]">
                Now live for revenue teams
              </span>
            </div>

            <h1 className="text-[clamp(2.2rem,5vw,3.85rem)] font-bold leading-[1.07] tracking-[-0.035em] text-white">
              <span className="text-[#0092B4]">Real-time intelligence</span>{" "}
              for every revenue call.
            </h1>

            <p className="mx-auto mt-6 max-w-lg text-[1rem] leading-relaxed text-slate-400 lg:mx-0">
              Shadow coaches your reps live during every call — and handles the CRM updates,
              follow-ups, and tasks automatically after it ends.
            </p>

            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row lg:items-start">
              <a
                href="mailto:hello@shadowlabs.ai?subject=Shadow Demo Request"
                onClick={() => posthog.capture("landing.book_demo.clicked", { location: "hero" })}
                className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[#0092B4] px-7 text-[15px] font-medium text-white
                  shadow-[0_0_20px_2px_rgba(0,146,180,0.2)]
                  transition-all duration-300
                  hover:bg-[#007a98] hover:shadow-[0_0_28px_6px_rgba(0,146,180,0.35)]
                  active:scale-[0.98]"
              >
                Book a Demo
                <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
              </a>
              <a
                href="https://github.com/Converse-AI-Inc/Shadow/releases/latest/download/Shadow.dmg"
                onClick={() => posthog.capture("landing.download_app.clicked", { location: "hero" })}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-white/20 px-7 text-[15px] font-medium text-slate-300 transition-all hover:border-white/35 hover:text-white"
              >
                Download App
              </a>
            </div>

            {/* Platform row — icon + text, below CTA */}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-4 lg:justify-start">
              <span className="text-[12px] text-slate-600">Works on</span>
              {PLATFORMS.map(({ name, Icon, color }) => (
                <div key={name} className="flex items-center gap-1.5">
                  <Icon />
                  <span className="text-[12.5px] font-medium" style={{ color }}>{name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Desktop app window ── */}
          <div className="relative w-full">
            <div
              className="pointer-events-none absolute -bottom-8 left-1/2 h-20 w-4/5 -translate-x-1/2 rounded-full blur-[44px]"
              style={{ background: "rgba(200,160,100,0.16)" }}
            />
            <DesktopAppWindow />
          </div>
        </div>
      </div>

    </section>
  );
}
